/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/**
 * The list of valid task list preference keys.
 *
 * NOTE: This would ideally be the only module where this enumeration is
 * defined, but because of some TypeScript strangeness, we cannot seem to
 * import it from the server module, so it is defined there, as well.
 *
 * If you make any changes here, you must also make them in
 * packages/server-ts/src/routes/rest/preferences/preferences-constants.ts.
 */
export enum TaskListKey {
  HAS_LEARNED_WATSON_ASSISTANT = 'hasLearnedWatsonAssistant',
  HAS_EXPLORED_LEARNING_CENTER = 'hasExploredLearningCenter',
  HAS_EXPLORED_INTERACTIVE_DEMO = 'hasExploredInteractiveDemo',
  // "Create a conversation" unique keys
  HAS_CREATED_FIRST_ACTION = 'hasCreatedFirstAction',
  HAS_CREATED_STEPS = 'hasCreatedSteps',
  HAS_ADDED_FIVE_EXAMPLES = 'hasAddedFiveExamples',
  // "Share with your team" unique keys
  HAS_PREVIEWED_ASSISTANT = 'hasPreviewedAssistant',
  HAS_SHARED_ASSISTANT = 'hasSharedAssistant', // (Shared by New IA)
  // (New IA - Task List) Keys unique to New IA Task List
  // "Enhance your assistant" unique keys
  HAS_VIEWED_GREETING_ACTION = 'hasViewedGreetingAction',
  HAS_BUILT_SECOND_ACTION = 'hasBuiltSecondAction',
  HAS_VIEWED_STEP_VALIDATION = 'hasViewedStepValidation',
  HAS_VIEWED_CLARIFYING_QUESTION = 'hasViewedClarifyingQuestion',
  HAS_VIEWED_RETRY_ACTION = 'hasViewedRetryAction',
  HAS_VIEWED_FALLBACK_ACTION = 'hasViewedFallbackAction',
  // "Test and refine your assistant" unique keys
  HAS_VIEWED_DEBUG_MODE = 'hasViewedDebugMode',
  HAS_VIEWED_ANALYTICS = 'hasViewedAnalytics',
  HAS_ADDITIONAL_ACTIONS = 'hasAdditionalActions',
  HAS_REFINED_WITH_FEEDBACK = 'hasRefinedWithFeedback',
  // "Deploy your assistant" unique keys
  HAS_VIEWED_INTEGRATIONS = 'hasViewedIntegrations',
  HAS_PUBLISHED_ASSISTANT = 'hasPublishedAssistant',
  // (New IA - Personalized Dashboard Task List) Additional keys unique to New IA Task List ("Deploy your assistant" section)
  // If user selected 'Web' in Onboarding Wizard:
  HAS_LEARNED_WEB_CHAT = 'hasLearnedWebChat',
  HAS_SET_UP_WEB_CHAT = 'hasSetUpWebChat',
  HAS_EMBEDDED_WEB_CHAT = 'hasEmbeddedWebChat',
  // If user selected 'Voice' in Onboarding Wizard:
  HAS_LEARNED_PHONE = 'hasLearnedPhone',
  HAS_SET_UP_PHONE = 'hasSetUpPhone',
  // If user selected 'SMS' in Onboarding Wizard:
  HAS_LEARNED_SMS = 'hasLearnedSMS',
  HAS_SET_UP_SMS = 'hasSetUpSMS',
  // If user selected 'Slack' in Onboarding Wizard:
  HAS_LEARNED_SLACK = 'hasLearnedSlack',
  HAS_SET_UP_SLACK = 'hasSetUpSlack',
  // If user selected 'Facebook' in Onboarding Wizard:
  HAS_LEARNED_FACEBOOK = 'hasLearnedFacebook',
  HAS_SET_UP_FACEBOOK = 'hasSetUpFacebook',
  // If user selected 'WhatsApp' in Onboarding Wizard:
  HAS_LEARNED_WHATS_APP = 'hasLearnedWhatsApp',
  HAS_SET_UP_WHATS_APP = 'hasSetUpWhatsApp',
  HAS_FINISHED_ONBOARDING_TASK_LIST = 'hasFinishedOnboardingTaskList',
  HAS_SEEN_ONBOARDING_CONGRATS_MODAL = 'hasSeenOnboardingCongratsModal',
  HAS_FINISHED_DASHBOARD_TASK_LIST = 'hasFinishedDashboardTaskList',
  // (THINK 2024 - Task List) Additional keys added for THINK 2024 home page
  HAS_SET_UP_SEARCH = 'hasSetUpSearch',
  HAS_VIEWED_SKILLS_CATALOG = 'hasViewedSkillsCatalog',
  HAS_VIEWED_WEB_CHAT = 'hasViewedWebChat',
  HAS_SET_UP_LIVE_AGENT = 'hasSetUpLiveAgent',
  TEXT_GENERATION = 'text_generation'
}

export const enum ImageUploadPreference {
  UPLOAD_JPG_PREFIX = 'upj-',
  UPLOAD_PNG_PREFIX = 'upp-',
  UPLOAD_UNTYPED_PREFIX = 'upx-',
  UPLOAD_COMMON_FRAGMENT = 'up'
}

export type Preferences = Record<string, string | number | boolean | object>;
export type UserPreferences = Preferences;
export type InstancePreferences = Preferences;

export type TaskListPreferences = Partial<Record<TaskListKey, boolean>>;

export type AssistantPreferences = {
  taskListStatus?: TaskListPreferences;
  personalizationWizard?: {
    channel?: string;
    industry?: string;
    role?: string;
    useCase?: string;
  };
  preview?: {
    backgroundUrl?: string;
  };
};

export type AssistantPreferencesWithId = {
  assistant_id: string;
  preferences: AssistantPreferences;
};

// The argument passed to the thunk.
export type InstancePreferencesArg = {
  preferences: InstancePreferences;
};
